import { MutationCache, QueryClient } from '@tanstack/vue-query'

/**
 * @typedef {import('@tanstack/vue-query').QueryClientConfig} QueryClientConfig
 * @typedef {import('@tanstack/vue-query').QueryKey} QueryKey
 */

/**
 * Custom Meta shape for Mutations
 * @typedef {Object} MutationMeta
 * @property {QueryKey} [invalidateQueries]
 */

/** @type {QueryClient | undefined} */
let browserQueryClient

/**
 * Creates a new QueryClient instance with default configurations.
 * * @param {QueryClientConfig} [config] - Optional configuration overrides.
 * @returns {QueryClient}
 */
function makeQueryClient(config) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
    },
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        /** @type {MutationMeta | undefined} */
        const meta = mutation.meta

        if (meta?.invalidateQueries) {
          browserQueryClient?.invalidateQueries({
            queryKey: meta.invalidateQueries,
          })
        }
      },
    }),
    ...config,
  })
}

/**
 * Singleton pattern to get or create the QueryClient.
 * @param {QueryClientConfig} [config]
 * @returns {QueryClient}
 */
export function getQueryClient(config) {
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient(config)
  }
  return browserQueryClient
}
